/**
 * The reports a project feeds, on Reports (N4b of the #55 plan, §B.15.5): a
 * non-profit's Statement of Functional Expenses, and the Programs (or
 * Projects) report for any business with a project. The figures are the
 * API's (apps/api/src/services/project-reports.ts); this lays them out.
 *
 * Each row's columns add up to that account's figure on the Statement of
 * Activities, the income statement or the balance sheet, and each account
 * opens its lines, as on those statements.
 */

import { Link } from 'react-router-dom';
import type { FunctionalColumns, FunctionalExpensesReport, ProjectReport, ProjectReportRow } from '../api.js';
import { formatCents } from '../lib/money.js';
import { InfoNote } from './InfoNote.js';

function linesHref(accountId: string, report: 'income' | 'balance', window: { from?: string; to: string }): string {
  const params = new URLSearchParams({ account: accountId, report, to: window.to });
  if (window.from) params.set('from', window.from);
  return `/reports/account?${params.toString()}`;
}

export function FunctionalExpensesStatement({ report }: { report: FunctionalExpensesReport }) {
  const { from, to } = report;
  const showMembership = report.hasMembershipDevelopment || report.total.membershipDevelopmentCents !== 0;
  // 0081 refuses a posted expense line with no program, so this column exists only to show one that got through.
  const showNoProgram = report.total.noProgramCents !== 0 || report.expenses.some((row) => row.noProgramCents !== 0);
  const width = 1 + report.programs.length + 3 + (showMembership ? 1 : 0) + (showNoProgram ? 1 : 0) + 1;
  const cells = (columns: FunctionalColumns) => (
    <>
      {columns.programCents.map((cents, index) => (
        <td key={report.programs[index]!.projectId} className="num">
          {formatCents(cents)}
        </td>
      ))}
      <td className="num">{formatCents(columns.programServicesCents)}</td>
      <td className="num">{formatCents(columns.managementAndGeneralCents)}</td>
      <td className="num">{formatCents(columns.fundraisingCents)}</td>
      {showMembership && <td className="num">{formatCents(columns.membershipDevelopmentCents)}</td>}
      {showNoProgram && <td className="num">{formatCents(columns.noProgramCents)}</td>}
      <td className="num">{formatCents(columns.totalCents)}</td>
    </>
  );
  return (
    <div className="report">
      <h2>Statement of Functional Expenses</h2>
      <p className="muted">
        {from} to {to}{' '}
        <InfoNote label="What The Columns Are">
          Each expense is in the column of the program its line names. Program Services adds up the programs; Management
          and General, Fundraising and Membership Development each add up the programs given that function in Settings.
          Each row&apos;s Total is that account&apos;s expense on the Statement of Activities for the same period.
          Splitting a shared cost between programs is done on the entry&apos;s lines.
        </InfoNote>
      </p>
      {showNoProgram && (
        <p role="alert" className="error">
          Some expenses name no program, which the books should never hold. Report it.
        </p>
      )}
      <div className="table-scroll">
        <table className="lines report-classes">
          <thead>
            <tr>
              <th />
              {report.programs.map((program) => (
                <th key={program.projectId} className="num">
                  {program.name}
                  {program.archived ? ' (Archived)' : ''}
                </th>
              ))}
              <th className="num">Program Services</th>
              <th className="num">Management and General</th>
              <th className="num">Fundraising</th>
              {showMembership && <th className="num">Membership Development</th>}
              {showNoProgram && <th className="num">No Program</th>}
              <th className="num">Total</th>
            </tr>
          </thead>
          <tbody>
            {report.expenses.map((row) => (
              <tr key={row.accountId}>
                <td>
                  <Link to={linesHref(row.accountId, 'income', { from, to })}>
                    {row.code} {row.name}
                  </Link>
                </td>
                {cells(row)}
              </tr>
            ))}
            {report.expenses.length === 0 && (
              <tr>
                <td className="muted" colSpan={width}>
                  No expenses posted in this period.
                </td>
              </tr>
            )}
            <tr className="report-total is-final">
              <td>Total expenses</td>
              {cells(report.total)}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * The Projects (or Programs) report, and since 0091 the phase report in the
 * same table: a phase report arrives with its columns renamed to project
 * shape, and `narrowedTo` names the project it reads with everything under it.
 */
export function ProjectReportTable({
  report,
  word,
  narrowedTo = null,
}: {
  report: ProjectReport;
  word: 'Program' | 'Project' | 'Phase';
  narrowedTo?: string | null;
}) {
  const { from, to } = report;
  const width = report.columns.length + 2;
  const total = (values: number[]) => values.reduce((sum, cents) => sum + cents, 0);
  const money = (values: number[]) => (
    <>
      {values.map((cents, index) => (
        <td key={report.columns[index]!.projectId ?? 'none'} className="num">
          {formatCents(cents)}
        </td>
      ))}
      <td className="num">{formatCents(total(values))}</td>
    </>
  );
  const rows = (list: ProjectReportRow[], kind: 'income' | 'balance', empty: string) => (
    <>
      {list.map((row) => (
        <tr key={row.accountId}>
          <td>
            <Link to={linesHref(row.accountId, kind, kind === 'income' ? { from, to } : { to })}>
              {row.code} {row.name}
            </Link>
          </td>
          {money(row.cells)}
        </tr>
      ))}
      {list.length === 0 && (
        <tr>
          <td className="muted" colSpan={width}>
            {empty}
          </td>
        </tr>
      )}
    </>
  );
  return (
    <div className="report">
      <h2>
        {word === 'Phase' ? 'By Phase' : `${word}s`}
        {narrowedTo !== null && `: ${narrowedTo}`}
      </h2>
      <p className="muted">
        {from} to {to}{' '}
        <InfoNote label={`What Cost To Date Is`}>
          Revenue and expenses are this period&apos;s, by the {word.toLowerCase()} each line names; No {word} is the
          lines that name none. Cost to date is every expense from the start of the books through {to}, plus what is
          still held on an asset that tracks projects as of that date, so a cost counts the same before and after it is
          expensed.{' '}
          {narrowedTo === null
            ? `Each account's Total is its figure on the ${word === 'Program' ? 'Statement of Activities' : 'income statement'} or the balance sheet.`
            : `Only the lines of ${narrowedTo} and every project under it are read, so each account's Total is theirs.`}
          {word === 'Phase' && ' Each phase is its own column: a phase with phases under it holds only its own lines, not theirs.'}
        </InfoNote>
      </p>
      <div className="table-scroll">
        <table className="lines report-classes">
          <thead>
            <tr>
              <th />
              {report.columns.map((column) => (
                <th key={column.projectId ?? 'none'} className="num">
                  {column.projectId === null ? `No ${word}` : column.name}
                  {column.archived ? ' (Archived)' : ''}
                </th>
              ))}
              <th className="num">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr className="report-section">
              <th colSpan={width}>Revenue</th>
            </tr>
            {rows(report.revenue, 'income', 'No revenue posted in this period.')}
            <tr className="report-total">
              <td>Total revenue</td>
              {money(report.totalRevenue)}
            </tr>

            <tr className="report-section">
              <th colSpan={width}>Expenses</th>
            </tr>
            {rows(report.expenses, 'income', 'No expenses posted in this period.')}
            <tr className="report-total">
              <td>Total expenses</td>
              {money(report.totalExpenses)}
            </tr>
            <tr className="report-total">
              <td>Revenue less expenses</td>
              {money(report.difference)}
            </tr>

            {report.held.length > 0 && (
              <>
                <tr className="report-section">
                  <th colSpan={width}>Held On Project Assets, As Of {to}</th>
                </tr>
                {rows(report.held, 'balance', '')}
              </>
            )}
            <tr className="report-total is-final">
              <td>Cost to date</td>
              {money(report.costToDate)}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
